import type { AxiosError } from 'axios';
import { defineStore } from 'pinia';

import { showFlash } from '@/alerts';
import { dashboardBffClient } from '@/api-client';
import type { LicenseStatus, MCLicenseUsage } from '@/api-client/api';
import constants from '@/constants';
import { error as logError } from '@/logger';
import { considerShowingNotifications, tooLongSinceValidLicense } from '@/utils/licenseWarnings';

export interface State {
  licenseStatus: LicenseStatus | null;
  lastCompliantTime: number;
  licenseUsage: MCLicenseUsage | null;
  usageError: AxiosError | null;
}

export const useLicensingStore = defineStore('licensing', {
  state: (): State => ({
    licenseStatus: null,
    lastCompliantTime: -Infinity,
    licenseUsage: null,
    usageError: null,
  }),
  getters: {
    licenseIsInvalid: (state) => {
      return state.licenseStatus !== null && !(state.licenseStatus.status === 'valid');
    },
    isPhoneHomeStatusInactive: (state) => {
      return (
        state.licenseStatus?.phoneHomeStatus === 'offlineMode' ||
        state.licenseStatus?.phoneHomeStatus === 'unreachableBucket'
      );
    },
    isGracePeriod(state): boolean {
      return (state.licenseStatus?.gracePeriod?.remainingMinutes ?? 0) > 0 && this.licenseIsInvalid;
    },
    isGracePeriodExpired(state): boolean {
      if (typeof state.licenseStatus?.gracePeriod?.remainingMinutes === 'undefined') return false;
      return state.licenseStatus?.gracePeriod?.remainingMinutes <= 0 && this.licenseIsInvalid;
    },
  },
  actions: {
    async fetchStatus() {
      try {
        const { data } = await dashboardBffClient.licenseStatus.getLicenseStatus();
        this.licenseStatus = data;
        if (!this.licenseIsInvalid) {
          this.lastCompliantTime = Date.now();
        }
        considerShowingNotifications();
      } catch (error: unknown) {
        const err = error as AxiosError;
        const statusCode = err.response?.status ?? 0;
        // If it hasn't been too long since the user started getting errors, then show the 5XX error.
        // Otherwise, show a message explaining that functionality is being restricted.
        if (statusCode >= 500 && tooLongSinceValidLicense(this.lastCompliantTime)) {
          const message = 'Unable to validate license';
          showFlash({
            message,
            details: `The Kasten licensing service is unavailable and this system's license could not be validated. Some functionality will be disabled. ${constants.messages.SUPPORT}`,
            messageType: 'danger',
            icon: 'icon-alert',
            dismiss: 14000,
          });
        } else {
          logError('error fetching license status', err);
        }
      }
    },
    async fetchUsage() {
      try {
        const { data } = await dashboardBffClient.mc.mcLicenseUsage();
        this.licenseUsage = data;
        this.usageError = null;
      } catch (error: unknown) {
        const err = error as AxiosError;
        logError('error fetching license usage', err);
        this.usageError = err;
      }
    },
  },
});
